import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import type { Zone } from '../../types';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  IconButton,
} from '@mui/material';
import { Home as HomeIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

type Farm = {
  id: string;
  name: string;
  user_id: string;
};

interface ZoneFormValues {
  id?: string;
  name: string;
  farm_id: string;
  user_id?: string;
  created_at?: string;
}

const ZoneForm: React.FC = () => {
  const { farmId, zoneId } = useParams<{ farmId: string; zoneId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [farm, setFarm] = useState<Farm | null>(null);
  const [zone, setZone] = useState<ZoneFormValues>({ name: '', farm_id: farmId || '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<Record<string, string>>({});

  // Load farm and zone data if in edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (!user || !farmId) return;
      
      try {
        setLoading(true);
        
        // Fetch farm
        const { data: farmData, error: farmError } = await api.farms.getById(farmId);
        if (farmError) throw farmError;
        if (!farmData) throw new Error('Farm not found');
        setFarm(farmData);

        // If in edit mode, fetch zone data
        if (zoneId) {
          const { data: zoneData, error: zoneError } = await api.zones.getById(zoneId);
          if (zoneError) throw zoneError;
          if (!zoneData) throw new Error('Zone not found');
          setZone(zoneData);
        } else {
          // Initialize new zone with farm_id
          setZone(prev => ({ ...prev, farm_id: farmId }));
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, farmId, zoneId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setZone(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (formError[name]) {
      setFormError(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!zone.name.trim()) {
      errors.name = 'Zone name is required';
    }
    
    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !user) return;
    
    try {
      setSaving(true);
      
      if (zoneId && typeof zoneId === 'string') {
        // Update existing zone
        const { error } = await api.zones.update(zoneId, zone.name);
        if (error) throw error;
        // Navigate back to zone detail
        navigate(`/farms/${farmId}/zones/${zoneId}`);
      } else if (farmId && typeof farmId === 'string') {
        // Create new zone
        const { data, error } = await api.zones.create(zone.name, farmId);
        if (error) throw error;
        if (data && data[0]) {
          // Navigate to the new zone's detail page
          navigate(`/farms/${farmId}/zones/${data[0].id}`);
        }
      }
    } catch (error) {
      console.error('Error saving zone:', error);
      setError('Failed to save zone. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!farm) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Farm not found.
      </Alert>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          component={Link}
          to="/farms"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Farms
        </MuiLink>
        <MuiLink
          component={Link}
          to={`/farms/${farmId}/zones`}
          color="inherit"
        >
          {farm.name}
        </MuiLink>
        <Typography color="text.primary">
          {zoneId ? 'Edit Zone' : 'Add New Zone'}
        </Typography>
      </Breadcrumbs>

      {/* Header with back button */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton 
          onClick={() => navigate(-1)} 
          sx={{ mr: 1 }}
          aria-label="go back"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {zoneId ? 'Edit Zone' : 'Add New Zone'}
        </Typography>
      </Box>

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            label="Zone Name"
            name="name"
            value={zone.name}
            onChange={handleInputChange}
            error={!!formError.name}
            helperText={formError.name || 'Enter a descriptive name for this zone (e.g., North Field, Greenhouse A, etc.)'}
            required
            disabled={saving}
          />
          
          <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
            >
              {saving ? 'Saving...' : zoneId ? 'Update Zone' : 'Create Zone'}
            </Button>
          </Box>
        </form>
      </Paper>
      
      {zoneId && (
        <Box mt={4}>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" color="error" gutterBottom>
            Danger Zone
          </Typography>
          <Paper sx={{ p: 3, backgroundColor: 'rgba(255, 0, 0, 0.02)', border: '1px solid rgba(255, 0, 0, 0.1)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1" color="error">
                  Delete this zone
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Once you delete a zone, there is no going back. Please be certain.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                onClick={() => navigate(`/farms/${farmId}/zones/${zoneId}`, { state: { showDelete: true } })}
              >
                Delete Zone
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default ZoneForm;
